// =============================================================================
// #1653 — reading her money back. The read half of the one-brain gap.
//
// kin-chat could offer to open the Budget Tracker and could not see one line in
// it. The prompt has a block telling Kin to offer that tool, so the failure was
// not a silence — it was Kin volunteering a thing she then could not discuss.
// "How much have I spent on Emma's party" had no answer on any text surface,
// while the number sat in `budget_items` the whole time.
//
// ── Why a tool and not a context block ──────────────────────────────────────
//
// The alternative was injecting budget context on every Pro turn. That is six
// queries (`budgets`, `budget_items`, `personal_budgets`, paychecks, bills,
// transactions) on turns about what's for dinner, and the keyword gate that
// would have avoided it cannot actually tell money apart: "am I over on the
// party?" has no money word in it, and "how much time do I have" has one. The
// model knows the intent and a tool lets it say so, so the read runs when money
// is in play and never otherwise. get_todos (#1802) set this shape two days
// before this file; this follows it deliberately rather than inventing a second
// one.
//
// ── ⚠️ THE FAILURE MODE THIS FILE IS SHAPED AROUND ──────────────────────────
//
// todoRead.ts is shaped around never reporting a failed read as an empty list.
// Money has a sharper version of the same trap, and it is the reason almost
// every flag below exists: A NUMBER IS ACTIONABLE AND A LIST IS NOT. If Kin
// says "you've spent $400 of your $2,000" off a truncated read, she does not
// hedge and neither does Kasandra — she spends the difference. An under-report
// here is not a cosmetic miss, it is a wrong decision made on Kin's authority.
//
// So three outcomes are kept apart all the way to the rendered text:
//   - the read FAILED            → never a zero, never a total
//   - the read was TRUNCATED     → every number is a FLOOR and is labelled one
//   - she has NO budget          → a real answer, said plainly
//
// And a partial failure is its own case. The two budgets are independent reads;
// if the personal half fails while projects come back, saying nothing about the
// personal half implies it is empty. `personalFailed` carries that to the text.
//
// ── Row shapes, introspected, not assumed ───────────────────────────────────
//
// Per the scheduledTaskBuilder.ts precedent. Both traps named in #1653's own
// comments are real and are handled here:
//
//   `budget_items` has NO `user_id`. It is scoped through its parent budget —
//   the exception CLAUDE.md rule 6 names — so every item read is `.in`
//   budget ids already filtered to her, and NOT by a user column that does not
//   exist. (A missing column on an RLS'd table surfaces as a 42501, not as
//   "column missing", so getting this wrong reads as a permissions bug.)
//
//   `personal_budget_bills` is TWO LEVELS DEEP: bill → paycheck → budget →
//   user. There is no `budget_id` on a bill. So bills need the paycheck ids
//   first, and when she has no paychecks the bills query is SKIPPED rather than
//   run with an empty `.in` list.
//
//   `personal_budget_bills` HAS a due day, since #1918: `due_day`, 1-31 with 32
//   meaning the last day of the month and null meaning she has not set one.
//   This comment used to assert the column did not exist. That was true when
//   #1653 shipped and false the moment the migration ran, which is why #1923
//   found Kin unable to name a bill or say when it was owed.
//
//   `expires_at` is a DIFFERENT question — when a bill stops applying, not when
//   it is owed — and the two go through different helpers (`billExpiry.ts` and
//   `payDay.ts`) so no reader has to keep them apart by hand.
//
// ── ⚠️ ONE RULE, TWO SURFACES ────────────────────────────────────────────────
//
// The expired-bill rule and the bill-to-paycheck matching are NOT implemented
// here. They live in `_shared/billExpiry.ts` and `_shared/billPaycheckMatch.ts`
// and the app's budget sheet draws with the same two modules. That is the whole
// point: this tool's numbers have to equal the ones on her screen, and a second
// copy of either rule is how the screen and Kin start disagreeing about the
// same month. `_shared/payDay.ts` moved to the Deno side in #1923 for exactly
// this reason.
//
//   In the tracker the line NAME is `category` — `budget_items` has no `name`
//   column. partyBudget.ts's header says the same thing for the write side.
// =============================================================================

import { DEFAULT_USER_TIMEZONE, todayYmdInZone } from "../_shared/localDate.ts";
import { billsCountingInMonth, isBillFinalMonth } from "../_shared/billExpiry.ts";
import {
  billsByDueDate,
  billsDueBeforeNextPayday,
  matchBillsToPaychecks,
  type MatchableBill,
  type MatchablePaycheck,
} from "../_shared/billPaycheckMatch.ts";
import { budgetNow, type NowTransaction } from "../_shared/budgetNow.ts";
import { dueDayLabel, ordinalLabel } from "../_shared/payDay.ts";
import { escapeIlikeWildcards } from "./ilikeEscape.ts";

export interface BudgetReadClient {
  from: (table: string) => any;
}

/**
 * How many of her project budgets to read. Far above any real account; the
 * biggest live account has a handful.
 */
export const BUDGET_READ_BUDGET_LIMIT = 50;

/**
 * How many budget lines to read across ALL her projects in one query.
 *
 * ⚠️ Account-wide, not per budget — one `.in(budget_id, ids)` query serves every
 * project, exactly as `useBudgets` does it. So when this cap bites, EVERY
 * project's totals are a floor, not just the one that happened to lose rows.
 * `capped` is therefore set on all of them; see `fetchBudgetRead`.
 */
export const BUDGET_READ_ITEM_LIMIT = 500;

/** Caps on the personal-budget reads. Same reasoning, same floor semantics. */
export const BUDGET_READ_PAYCHECK_LIMIT = 50;
export const BUDGET_READ_BILL_LIMIT = 500;
export const BUDGET_READ_TX_LIMIT = 500;

/** How many individual lines to name per project in the rendered text. */
export const BUDGET_READ_LINES_SHOWN = 5;

/**
 * How many bills to NAME in the monthly section, across every paycheck.
 *
 * ⚠️ An account-wide allowance, not a per-paycheck one, and that is deliberate:
 * `BUDGET_READ_PAYCHECK_LIMIT` is 50, so a per-paycheck cap of 20 would let one
 * account emit a thousand lines into a tool result. The allowance is spent
 * walking the paychecks in order and whatever is left over is reported as a
 * count, so the section is bounded however her month is shaped.
 *
 * Far above any real account: a month has a handful of paychecks and on the
 * order of fifteen bills, so nothing is omitted in practice. This is a bound,
 * not a feature.
 *
 * ⚠️ It caps the NAMES ONLY. Every total below is computed over all of her
 * bills, so a capped list never moves a number. Naming fewer bills than she has
 * must not change what she is told she owes.
 */
export const BUDGET_READ_BILLS_SHOWN = 40;

export interface BudgetLineView {
  /** Her label for the line. This is `category` — see the header. */
  label: string;
  estimated: number;
  spent: number;
}

export interface ProjectBudgetView {
  name: string;
  /**
   * What she set as the total, or null when she never set one.
   *
   * ⚠️ null is NOT zero, and the difference matters in the text. The app coerces
   * with `Number(b.total_budget) || 0` and then renders `remaining = 0 - spent`,
   * so a budget with no total shows as over by everything she has spent. That is
   * survivable as a number on screen next to an empty total field; it is not
   * survivable as Kin saying "you're $400 over". So null suppresses remaining
   * entirely rather than being floored to 0.
   */
  total: number | null;
  estimated: number;
  /** Sum of `actual_cost` — what the tracker labels "Spent". */
  spent: number;
  lineCount: number;
  topLines: BudgetLineView[];
  /** The item read hit its cap, so `spent` and `estimated` are FLOORS. */
  capped: boolean;
  completed: boolean;
  /**
   * How many of her budgets share this name, when more than one does.
   *
   * ⚠️ Same honesty rule as todoRead's `duplicateNameCount`. Nothing stops two
   * budgets called "Party", and a named read resolves to ONE. Reporting its
   * total as "your Party budget" would hide the other one's spending.
   */
  duplicateNameCount?: number;
}

/** One bill, as Kin says it: "Mortgage $1,200, due the 5th". */
export interface BudgetBillView {
  name: string;
  amount: number;
  /** "5th", "Last day", or "" when she has not set a due day. */
  dueLabel: string;
  /**
   * This month is the bill's LAST one.
   *
   * The sheet shows a "Final month" pill on exactly these, so Kin carries it
   * too: a bill that counts in full this month and is gone next month is
   * something she wants told, and the alternative is Kin reporting a figure
   * that drops next month with no explanation.
   */
  finalMonth: boolean;
}

/** A paycheck and the bills it covers, matched live. See `billPaycheckMatch`. */
export interface PaycheckBillsView {
  name: string;
  amount: number;
  /** Named bills, capped by `BUDGET_READ_BILLS_SHOWN`. */
  bills: BudgetBillView[];
  /** Matched here but NOT named above, because the render allowance ran out. */
  billsOmitted: number;
  /** Sum of EVERY bill matched here, including any not named. */
  billTotal: number;
}

/**
 * ⚠️ THERE IS NO PER-PAYCHECK LEFTOVER HERE, AND THERE MUST NOT BE ONE.
 *
 * The first version of this file carried one (`amount - its matched bills`),
 * mirroring the sheet's per-paycheck card, and shipped it with a caveat
 * explaining that it ignores transactions. That was wrong twice over:
 *
 * 1. THE NUMBER IS WRONG IN THE EXPENSIVE DIRECTION. It omits spending
 *    transactions and bills with no due day, so the per-paycheck figures sum
 *    HIGHER than the month's real leftover. On the worked fixture they came to
 *    $2,190 against a true $2,000: Kin would tell her she had $190 more than
 *    she did, and an over-report is the one error that makes her spend it.
 *
 * 2. A CAVEAT CANNOT RETRACT A NUMBER. This file already learned that in PR
 *    #1809's review, which is why the capped branch SUPPRESSES figures instead
 *    of annotating them. Emitting the figure and explaining it afterwards
 *    repeats the mistake one branch over.
 *
 * So the only "what is left" this tool states is the MONTH's, and the useful
 * per-paycheck question is answered as a LIST instead: what is still owed
 * before the next paycheck lands. See `billsDueBeforeNextPayday`.
 */

export interface PersonalBudgetView {
  /** The month this is for, YYYY-MM-01, as `personal_budgets.month` stores it. */
  month: string;
  paycheckTotal: number;
  incomeTotal: number;
  billTotal: number;
  expenseTotal: number;
  /** paychecks + income transactions, exactly as PersonalBudget.tsx's totalIn. */
  totalIn: number;
  /** bills + expense transactions, exactly as its totalOut. */
  totalOut: number;
  /** totalIn - totalOut, its `leftOver`. */
  leftOver: number;
  billCount: number;
  paycheckCount: number;
  /** Any of the three personal reads hit its cap → every total here is a floor. */
  capped: boolean;

  /**
   * Her bills grouped under the paycheck that covers them, worked out live from
   * due days and pay days by the shared matcher.
   *
   * ⚠️ NEVER grouped by `personal_budget_bills.paycheck_id`. That column is NOT
   * NULL and is the FK chain RLS traverses to reach a user_id, so it is an
   * ownership anchor rather than an assignment — and the setup flow puts every
   * bill it creates on the FIRST paycheck. Grouping by it would put her whole
   * month under paycheck one and report the others as having nothing to pay.
   */
  groups: PaycheckBillsView[];

  /**
   * Bills she has not given a due day. They belong to no paycheck by design,
   * and they ARE still counted in `billTotal`.
   */
  noDueDate: BudgetBillView[];
  noDueDateTotal: number;
  noDueDateOmitted: number;

  /**
   * No paycheck has a `pay_date`, so there is nothing to match against.
   *
   * Callers must not invent pay dates. The matcher returns empty groups in this
   * state and `billsWithoutPayDays` carries the bills instead.
   */
  payDaysUnknown: boolean;

  /**
   * ⚠️ Every dated bill, in due-day order, ONLY when `payDaysUnknown`.
   *
   * Load-bearing. With no pay dates the matcher assigns no dated bill to any
   * group, so without this list those bills would be absent from the text while
   * still counting in `billTotal` — Kin saying "$2,200 going out in 12 bills"
   * and able to name none of them. The sheet has the same fallback
   * (`flatWhenUnknown` in PersonalBudget.tsx) for the same reason.
   */
  billsWithoutPayDays: BudgetBillView[];
  billsWithoutPayDaysOmitted: number;

  /**
   * The day of the month her next paycheck lands, or null when no pay day is
   * saved. `nextPayDayWraps` means it falls in the following month.
   */
  nextPayDay: number | null;
  nextPayDayWraps: boolean;
  /**
   * What is owed between today and that pay day, soonest first.
   *
   * ⚠️ Deliberately a LIST and not a balance. It answers "what's due before my
   * next payday", which is the question a per-paycheck leftover was a bad proxy
   * for, and it makes no claim about money available.
   */
  dueBeforeNextPayDay: Array<BudgetBillView & { nextMonth: boolean }>;
  dueBeforeNextPayDayTotal: number;
  dueBeforeNextPayDayOmitted: number;
  /** Today, in HER zone. The window is measured from this, never from UTC. */
  todayDay: number;

  /**
   * What she has RIGHT NOW: money in so far minus money out so far.
   *
   * ⚠️ `null` means not computable, and then NO number may be stated. It is not
   * a zero. See `nowUnavailable`.
   *
   * ⚠️ NOT A BANK BALANCE. Each month starts at $0 with no rollover, so this is
   * "what has moved through this month's budget so far". Copy must never imply
   * otherwise.
   */
  rightNow: number | null;
  /** `rightNow` minus the bills still to come before the next paycheck. */
  planOn: number | null;
  /** Why both are null, or null when they are fine. */
  nowUnavailable: null | "no-pay-days";
  /** The deduction behind `planOn`, so the figure can be explained. */
  stillToComeOut: number;
  /** The part of that owed by bills with no due day. See `budgetNow`. */
  stillToComeUndated: number;
}

export type BudgetReadOutcome =
  /**
   * At least one half of her money read cleanly.
   *
   * `projects` may legitimately be empty and `personal` may legitimately be null
   * — both are real answers. The two `*Failed` flags are what keep "nothing
   * there" and "could not look" apart per half.
   */
  | {
      kind: "ok";
      projects: ProjectBudgetView[];
      personal: PersonalBudgetView | null;
      projectsFailed: boolean;
      personalFailed: boolean;
      /**
       * She has MORE budgets than `projects` holds.
       *
       * ⚠️ Separate from a project's own `capped`, which is about its lines. This
       * one makes the COUNT a floor, so "you have 50 budgets" and the list itself
       * both become partial — and an absence among them stops being evidence.
       */
      projectsTruncated: boolean;
    }
  /** She named a budget she does not have. `available` is what she DOES have. */
  | {
      kind: "miss";
      requested: string;
      available: string[];
      /**
       * `available` is not all of them.
       *
       * ⚠️ `fetchBudgetNames` has always returned this and the miss branch used to
       * THROW IT AWAY, so with more than 50 budgets Kin said "Hers are: …" as an
       * exhaustive list with names missing from it. Building a sentinel and then
       * discarding it is the shape that looks handled in review and is not.
       */
      availableTruncated: boolean;
    }
  /** Both halves failed. ⚠️ NOT the same as her having no budgets. */
  | { kind: "failed" };

/**
 * Case-insensitive, whole-name budget matching.
 *
 * ⚠️ Deliberately the same rule as todoRead's `listNameMatches` — an exact
 * case-insensitive compare, no substring, no wildcards. A read that matched more
 * loosely than that would answer about the wrong budget with total confidence,
 * and "party" matching "Party supplies" is exactly how that happens.
 */
export function budgetNameMatches(name: unknown, requested: string): boolean {
  if (typeof name !== "string") return false;
  return name.trim().toLowerCase() === requested.trim().toLowerCase();
}

/**
 * The month key for her clock, as `personal_budgets.month` stores it.
 *
 * ⚠️ HER zone, not UTC, and this is not theoretical: at 9pm Eastern on Oct 31 it
 * is already Nov 1 in UTC, so a UTC month key would read November's budget —
 * which does not exist yet — and Kin would tell her she has no budget set up on
 * the night she most wants to look at it. Same lesson PR #1803's review caught
 * in get_todos, one unit up.
 */
export function monthKeyInZone(instant: Date, timeZone: string): string {
  const ymd = todayYmdInZone(instant, timeZone);
  return `${ymd.slice(0, 7)}-01`;
}

/** Coerce a PostgREST numeric to a usable number. Same guard the app uses. */
function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * An empty draft budget — the app DELETES these on every fetch.
 *
 * ⚠️ `useBudgets.fetchAll` treats a budget with no name, no total and no items as
 * garbage from an abandoned create and removes it. So those rows exist in the
 * table between app opens, and a read that counted them would have Kin say "you
 * have four budgets" about a tracker showing two.
 *
 * This read SKIPS them. It does not delete them — a read tool acquires no write
 * path, and the app's own cleanup gets there on her next open anyway.
 */
function isEmptyDraft(name: string, total: number | null, lineCount: number): boolean {
  return name.trim() === "" && (total === null || total === 0) && lineCount === 0;
}

function failedRead(where: string, err: unknown): void {
  console.error(`[get_budget] ${where}:`, err);
}

interface RawBudget {
  id?: unknown;
  name?: unknown;
  total_budget?: unknown;
  completed_at?: unknown;
}

interface RawItem {
  budget_id?: unknown;
  category?: unknown;
  estimated_cost?: unknown;
  actual_cost?: unknown;
}

/**
 * Her budget NAMES, for the sentence that follows a miss.
 *
 * ⚠️ Separate from the main read on purpose. A miss is a claim about her data, and
 * the names it offers her must come from an unfiltered read — the name-filtered
 * query that produced the miss returned nothing by definition. Bounded and
 * sentinelled like the others, so "she has more than this" stays sayable.
 */
async function fetchBudgetNames(
  client: BudgetReadClient,
  userId: string,
): Promise<{ names: string[]; truncated: boolean; failed: boolean }> {
  try {
    const { data, error } = await client
      .from("budgets")
      .select("name")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(BUDGET_READ_BUDGET_LIMIT + 1);
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    const truncated = rows.length > BUDGET_READ_BUDGET_LIMIT;
    const names = rows
      .slice(0, BUDGET_READ_BUDGET_LIMIT)
      .map((b: any) => (typeof b.name === "string" ? b.name.trim() : ""))
      .filter((n: string) => n);
    return { names, truncated, failed: false };
  } catch (err) {
    failedRead("budget names", err);
    return { names: [], truncated: false, failed: true };
  }
}

/**
 * Read her money.
 *
 * `requestedName` is her budget name when she named one, "" otherwise. A named
 * read covers the PROJECT budgets only and costs two queries; the overview also
 * reads the current month's personal budget and costs up to six. Naming one is
 * therefore both more precise and cheaper, which is why the tool description
 * pushes the model toward passing a name whenever she said one.
 */
export async function fetchBudgetRead(
  client: BudgetReadClient,
  userId: string,
  requestedName = "",
  now: Date = new Date(),
  timeZone: string = DEFAULT_USER_TIMEZONE,
): Promise<BudgetReadOutcome> {
  const named = requestedName.trim();

  // ── The project budgets ──────────────────────────────────────────────────
  //
  // ⚠️ A NAMED read filters IN THE DATABASE, and that is a correctness rule
  // rather than an optimisation. PR #1809's review caught the first version
  // reading the newest 50 and matching the name in memory afterwards: with 51
  // budgets, the one she asked about could fall outside the window and the answer
  // became "you have no budget called Emma's party" — a confident false claim
  // about her own data, produced by a cap she cannot see. The cap must never be
  // able to manufacture a miss.
  //
  // ⚠️ `.ilike` with the pattern escaped MUST AGREE WITH create_todo's list
  // matching (`index.ts` ~:1906, the same helper). An exact case-insensitive
  // compare with wildcards escaped, so a name containing % or _ matches itself
  // and nothing else, and the read resolves the same name the write would.
  let budgetRows: RawBudget[] = [];
  let projectsTruncated = false;
  let projectsFailed = false;
  try {
    let q = client
      .from("budgets")
      .select("id, name, total_budget, completed_at")
      .eq("user_id", userId);
    if (named) q = q.ilike("name", escapeIlikeWildcards(named));
    // ⚠️ LIMIT + 1 is a sentinel, not an off-by-one. `rows.length >= LIMIT` cannot
    // tell "exactly 50" from "50 of 200", so the overview used to report an exact
    // count and an exact list built from a silently truncated read. One extra row
    // answers the question the cap otherwise hides.
    const { data, error } = await q
      .order("updated_at", { ascending: false })
      .limit(BUDGET_READ_BUDGET_LIMIT + 1);
    if (error) throw error;
    const rows = Array.isArray(data) ? (data as RawBudget[]) : [];
    projectsTruncated = rows.length > BUDGET_READ_BUDGET_LIMIT;
    budgetRows = projectsTruncated ? rows.slice(0, BUDGET_READ_BUDGET_LIMIT) : rows;
  } catch (err) {
    failedRead("budgets", err);
    projectsFailed = true;
  }

  const nameCounts = new Map<string, number>();
  for (const b of budgetRows) {
    const n = typeof b.name === "string" ? b.name.trim().toLowerCase() : "";
    if (!n) continue;
    nameCounts.set(n, (nameCounts.get(n) || 0) + 1);
  }

  // ── Resolve a named read BEFORE reading any lines ────────────────────────
  //
  // ⚠️ So its cap is genuinely its own. The item read is one
  // `.in("budget_id", ids)` query, and the first version of this file passed
  // EVERY budget's id even when she had named one — which makes the 500-line cap
  // shared, and `sort_order` is per budget rather than global, so an unrelated
  // reno's lines can push the party's out of the window completely. The result
  // is `capped` on the one budget she asked about and a spend under-reported by
  // whatever fell off. PR #1803's review caught exactly this in get_todos; the
  // fix there and here is to scope the QUERY, not to filter afterwards.
  let targetRows = budgetRows;
  if (named) {
    if (projectsFailed) return { kind: "failed" };
    // The query already filtered by name; this is the exact re-check the
    // read/write parity test pins, and it costs nothing.
    targetRows = budgetRows.filter((b) => budgetNameMatches(b.name, named));
    if (targetRows.length === 0) {
      // ⚠️ A miss claims something about her data — "you have no budget called X"
      // — so the names it offers instead cannot come from the name-filtered query
      // that just returned nothing. This is its own bounded read.
      const { names, truncated, failed } = await fetchBudgetNames(client, userId);
      if (failed) return { kind: "failed" };
      return { kind: "miss", requested: named, available: names, availableTruncated: truncated };
    }
  }

  const ids = targetRows
    .map((b) => (typeof b.id === "string" ? b.id : null))
    .filter((v): v is string => !!v);

  let itemRows: RawItem[] = [];
  let itemsCapped = false;
  if (!projectsFailed && ids.length > 0) {
    try {
      const { data, error } = await client
        .from("budget_items")
        // ⚠️ No user_id on this table — scoped through the parent budget. See header.
        .select("budget_id, category, estimated_cost, actual_cost")
        .in("budget_id", ids)
        .order("sort_order", { ascending: true })
        .limit(BUDGET_READ_ITEM_LIMIT + 1);
      if (error) throw error;
      const rows = Array.isArray(data) ? (data as RawItem[]) : [];
      // ⚠️ Sentinel again. `>= LIMIT` flagged "exactly at the cap" as truncated,
      // which cried floor on a complete read, and could not flag a read that lost
      // hundreds of rows any more loudly than one that lost none.
      itemsCapped = rows.length > BUDGET_READ_ITEM_LIMIT;
      itemRows = itemsCapped ? rows.slice(0, BUDGET_READ_ITEM_LIMIT) : rows;
    } catch (err) {
      // ⚠️ The budgets read succeeded and the items read did not, so every total
      // would be zero against a real total_budget — "you've spent nothing of
      // your $2,000". That is the exact wrong number, so the whole project half
      // is failed rather than reported with empty lines.
      //
      // ⚠️ These two lines are a mutation-catalogue anchor
      // (1653-failed-items-read-reports-nothing-spent). Keep them adjacent.
      failedRead("budget_items", err);
      projectsFailed = true;
    }
  }

  const byBudget = new Map<string, RawItem[]>();
  for (const it of itemRows) {
    const key = typeof it.budget_id === "string" ? it.budget_id : "";
    if (!key) continue;
    const list = byBudget.get(key);
    if (list) list.push(it);
    else byBudget.set(key, [it]);
  }

  const projects: ProjectBudgetView[] = [];
  if (!projectsFailed) {
    for (const b of targetRows) {
      const id = typeof b.id === "string" ? b.id : "";
      const rawName = typeof b.name === "string" ? b.name : "";
      const lines = id ? byBudget.get(id) || [] : [];
      const total =
        b.total_budget === null || b.total_budget === undefined ? null : num(b.total_budget);

      if (isEmptyDraft(rawName, total, lines.length)) continue;

      const view: ProjectBudgetView = {
        name: rawName.trim() || "Untitled budget",
        total,
        estimated: lines.reduce((s, i) => s + num(i.estimated_cost), 0),
        spent: lines.reduce((s, i) => s + num(i.actual_cost), 0),
        lineCount: lines.length,
        // ⚠️ Sorted by SPEND before slicing, which is what makes the rendered
        // label "biggest lines by spend" true. The query orders by `sort_order`
        // (her manual ordering in the tracker), so slicing that directly showed
        // arbitrary rows under a superlative.
        //
        // The copy is DEFENSIVE, not load-bearing, and saying so is the honest
        // version: every other use of `lines` is order-independent (two reduces
        // and a `.length`), so mutating it in place would break nothing today —
        // a mutation proving exactly that survived, which is how this comment got
        // corrected. It stays a copy because the next reader who adds an
        // order-sensitive use should not have to discover this.
        topLines: [...lines]
          .sort((a, b) => num(b.actual_cost) - num(a.actual_cost) || num(b.estimated_cost) - num(a.estimated_cost))
          .slice(0, BUDGET_READ_LINES_SHOWN)
          .map((i) => ({
            label: (typeof i.category === "string" ? i.category.trim() : "") || "Untitled line",
            estimated: num(i.estimated_cost),
            spent: num(i.actual_cost),
          })),
        capped: itemsCapped,
        completed: !!b.completed_at,
      };
      const dupes = nameCounts.get(rawName.trim().toLowerCase()) || 0;
      if (dupes > 1) view.duplicateNameCount = dupes;
      projects.push(view);
    }
  }

  // ── A named read stops here, at the budgets it resolved to ───────────────
  if (named) {
    if (projectsFailed) return { kind: "failed" };
    return {
      kind: "ok",
      // ⚠️ EVERY same-named budget, not the first. Picking one and calling it
      // "your Party budget" is the failure `duplicateNameCount` warns about;
      // returning both lets Kin ask which she means with real numbers attached.
      projects,
      personal: null,
      projectsFailed: false,
      personalFailed: false,
      // The query was name-filtered, so a cap here would mean 50+ budgets sharing
      // one name. Reported rather than assumed away.
      projectsTruncated,
    };
  }

  // ── The personal monthly budget ──────────────────────────────────────────
  const monthKey = monthKeyInZone(now, timeZone);
  let personal: PersonalBudgetView | null = null;
  let personalFailed = false;
  try {
    const { data: pb, error: pbErr } = await client
      .from("personal_budgets")
      .select("id, month")
      .eq("user_id", userId)
      .eq("month", monthKey)
      .maybeSingle();
    if (pbErr) throw pbErr;

    const pbId = pb && typeof pb.id === "string" ? pb.id : "";
    if (pbId) {
      const { data: pcData, error: pcErr } = await client
        .from("personal_budget_paychecks")
        // ⚠️ `name`, `pay_date` and `sort_order` are LOAD-BEARING, not
        // decoration. The matcher orders paychecks by `sort_order` and anchors
        // bills on `pay_date`, and `name` is how Kin says which paycheck she
        // means. Note that ordering the QUERY by sort_order does not put the
        // column in the ROW: without it the matcher's comparator computes
        // `undefined - undefined`, which is NaN, and a NaN comparator leaves
        // the order alone. It would look right today purely because the query
        // happens to sort the same way, and break the moment that changed.
        .select("id, name, amount, pay_date, sort_order")
        .eq("budget_id", pbId)
        .order("sort_order", { ascending: true })
        .limit(BUDGET_READ_PAYCHECK_LIMIT + 1);
      if (pcErr) throw pcErr;
      const pcRows = Array.isArray(pcData) ? pcData : [];
      const paychecksCapped = pcRows.length > BUDGET_READ_PAYCHECK_LIMIT;
      const paychecks = paychecksCapped ? pcRows.slice(0, BUDGET_READ_PAYCHECK_LIMIT) : pcRows;
      const pcIds = paychecks
        .map((p: any) => (typeof p.id === "string" ? p.id : null))
        .filter((v: string | null): v is string => !!v);

      // ⚠️ SKIPPED when she has no paychecks. Bills hang off a paycheck, not off
      // the budget, so with no paycheck ids there is nothing to ask for — and
      // `.in("paycheck_id", [])` is a query whose meaning is worth nobody's time
      // to confirm. PersonalBudget.tsx guards it the same way.
      let bills: any[] = [];
      let billsCapped = false;
      if (pcIds.length > 0) {
        const { data: blData, error: blErr } = await client
          .from("personal_budget_bills")
          // ⚠️ Every column here is load-bearing and two of them fail SILENTLY
          // if dropped, which is why a test pins this list.
          //   `expires_at`: `billsCountingInMonth` FAILS OPEN, so a row fetched
          //     without the column is indistinguishable from a bill that never
          //     ends, and every finished bill quietly returns to her "going
          //     out". That is the exact defect this issue exists to fix.
          //   `due_day`: absent, every bill falls into the no-due-date group,
          //     which reads as her never having set a due date on anything.
          // Neither throws and neither fails a type check.
          .select("id, paycheck_id, name, amount, due_day, expires_at")
          .in("paycheck_id", pcIds)
          .order("sort_order", { ascending: true })
          .limit(BUDGET_READ_BILL_LIMIT + 1);
        if (blErr) throw blErr;
        const blRows = Array.isArray(blData) ? blData : [];
        billsCapped = blRows.length > BUDGET_READ_BILL_LIMIT;
        bills = billsCapped ? blRows.slice(0, BUDGET_READ_BILL_LIMIT) : blRows;
      }

      const { data: txData, error: txErr } = await client
        .from("personal_budget_transactions")
        // ⚠️ `transaction_date` is load-bearing and fails SILENTLY if dropped.
        // `budgetNow` treats an unreadable date as "spending has happened,
        // income has not", so a row fetched without the column still produces
        // a number: every expense would count as already out and every income
        // as not yet in. Lower than the truth, so nothing looks broken, and
        // the figure is simply wrong. A test pins the column for that reason.
        .select("amount, type, transaction_date")
        .eq("budget_id", pbId)
        .limit(BUDGET_READ_TX_LIMIT + 1);
      if (txErr) throw txErr;
      const txRows = Array.isArray(txData) ? txData : [];
      const txsCapped = txRows.length > BUDGET_READ_TX_LIMIT;
      const txs = txsCapped ? txRows.slice(0, BUDGET_READ_TX_LIMIT) : txRows;

      // ── Normalised at the boundary ───────────────────────────────────
      //
      // Per this file's header: PostgREST rows arrive as `any`, and the shared
      // matcher sorts on `name` and `sort_order`. A null in either would throw
      // inside a comparator rather than here, so an untidy row would surface as
      // a FAILED personal half. Both columns are NOT NULL in the schema today;
      // normalising costs nothing and does not depend on that staying true.
      const matchPaychecks: MatchablePaycheck[] = paychecks.map((p: any) => ({
        id: typeof p.id === "string" ? p.id : "",
        name: typeof p.name === "string" ? p.name : "",
        amount: num(p.amount),
        pay_date: typeof p.pay_date === "string" ? p.pay_date : null,
        sort_order: num(p.sort_order),
      }));

      // ⚠️ `amount` narrowed to `number`. The shared matcher accepts
      // `number | string` because PostgREST can hand back either, and this file
      // has already coerced it through `num()` — so keeping the union here
      // would make every total below `string | number` and the arithmetic
      // unsound. Narrowing still satisfies the matcher's `B extends
      // MatchableBill` constraint, since `number` is assignable to the union.
      type BillRow = Omit<MatchableBill, "amount"> & {
        amount: number;
        expires_at: string | null;
      };
      const allBills: BillRow[] = bills.map((b: any) => ({
        id: typeof b.id === "string" ? b.id : "",
        paycheck_id: typeof b.paycheck_id === "string" ? b.paycheck_id : "",
        name: typeof b.name === "string" ? b.name : "",
        amount: num(b.amount),
        // ⚠️ null stays null. `num()` would make it 0, and 0 is not a day: it
        // would read as a real due day that no month has and sort before the
        // 1st. "No due date yet" is a distinct answer from "due the 0th".
        due_day:
          b.due_day === null || b.due_day === undefined || !Number.isFinite(Number(b.due_day))
            ? null
            : Number(b.due_day),
        expires_at: typeof b.expires_at === "string" ? b.expires_at : null,
      }));

      // ⚠️ THE EXPIRED-BILL RULE, through the helper the sheet draws with. A
      // bill that finished in an earlier month is not money going out this
      // month, and this read used to count it — the same defect 79580cc0 fixed
      // on screen, which left Kin's "going out" higher than the number in front
      // of her. Compared against the month being READ, never against today.
      const liveBills = billsCountingInMonth(allBills, monthKey);

      const paycheckTotal = matchPaychecks.reduce((s, p) => s + num(p.amount), 0);
      const incomeTotal = txs
        .filter((t: any) => t.type === "income")
        .reduce((s: number, t: any) => s + num(t.amount), 0);
      const billTotal = liveBills.reduce((s, b) => s + b.amount, 0);
      const expenseTotal = txs
        .filter((t: any) => t.type === "expense")
        .reduce((s: number, t: any) => s + num(t.amount), 0);
      const totalIn = paycheckTotal + incomeTotal;
      const totalOut = billTotal + expenseTotal;

      // ── Which paycheck covers which bill, worked out live ─────────────
      const match = matchBillsToPaychecks(matchPaychecks, liveBills, monthKey);

      const billView = (b: BillRow): BudgetBillView => ({
        name: b.name.trim() || "Untitled bill",
        amount: b.amount,
        dueLabel: dueDayLabel(b.due_day),
        finalMonth: isBillFinalMonth(b.expires_at, monthKey),
      });

      // The naming allowance, spent walking the paychecks in order. It caps
      // NAMES only; every total below is computed over all of her bills.
      let nameBudget = BUDGET_READ_BILLS_SHOWN;
      const take = <T>(rows: T[]): { shown: T[]; omitted: number } => {
        const shown = rows.slice(0, Math.max(0, nameBudget));
        nameBudget -= shown.length;
        return { shown, omitted: rows.length - shown.length };
      };

      const groups: PaycheckBillsView[] = match.groups.map((g) => {
        const { shown, omitted } = take(g.bills);
        return {
          name: g.paycheck.name.trim() || "Paycheck",
          amount: num(g.paycheck.amount),
          bills: shown.map(billView),
          billsOmitted: omitted,
          // ⚠️ From the matcher, over EVERY bill matched here, so the naming
          // allowance above cannot move it.
          //
          // `g.leftover` is deliberately NOT read. The shared matcher still
          // computes it because the sheet's cards draw it today, but this tool
          // must never state it — see the note above `PaycheckBillsView`.
          billTotal: g.billTotal,
        };
      });

      // ⚠️ Only when NO pay day is known anywhere. Without this list those
      // bills count in `billTotal` and appear nowhere in the text at all.
      const flat = match.payDaysUnknown
        ? billsByDueDate(liveBills.filter((b) => b.due_day !== null), monthKey)
        : [];
      const flatTaken = take(flat);
      const noDueTaken = take(match.noDueDate);

      // ── What is owed before the next paycheck ─────────────────────────
      //
      // ⚠️ TODAY IN HER ZONE, from the same instant and zone `monthKey` came
      // from, so the day and the month cannot describe different days. A UTC
      // day here would roll over five hours early for her and quietly move the
      // window past a bill due that evening.
      const todayDay = Number(todayYmdInZone(now, timeZone).slice(8, 10));
      const payday = billsDueBeforeNextPayday(matchPaychecks, liveBills, monthKey, todayDay);
      const dueBeforeTaken = take(payday.dueBefore);

      // ── Right now, and what she can plan on (#1929) ───────────────────
      //
      // ⚠️ `allBills`, not `liveBills`. `budgetNow` applies the expiry rule
      // itself, so handing it the pre-filtered list would be harmless today and
      // would silently double-filter if either rule ever changed. One owner per
      // rule.
      //
      // The same shared helper the My Budget screen draws its headline from, so
      // Kin and the screen cannot differ about what she has today.
      const nowTx: NowTransaction[] = txs.map((t: any) => ({
        amount: num(t.amount),
        type: typeof t.type === "string" ? t.type : "",
        transaction_date: typeof t.transaction_date === "string" ? t.transaction_date : null,
      }));
      const nowView = budgetNow(matchPaychecks, allBills, nowTx, monthKey, todayDay);

      personal = {
        month: monthKey,
        paycheckTotal,
        incomeTotal,
        billTotal,
        expenseTotal,
        totalIn,
        totalOut,
        leftOver: totalIn - totalOut,
        // ⚠️ The LIVE count, not `bills.length`. A finished bill is not one of
        // this month's bills, and counting it here while excluding it from
        // `billTotal` would have Kin say "12 bills, $2,200" about eleven.
        billCount: liveBills.length,
        paycheckCount: matchPaychecks.length,
        capped: paychecksCapped || billsCapped || txsCapped,
        groups,
        noDueDate: noDueTaken.shown.map(billView),
        noDueDateTotal: match.noDueDate.reduce((s, b) => s + b.amount, 0),
        noDueDateOmitted: noDueTaken.omitted,
        payDaysUnknown: match.payDaysUnknown,
        billsWithoutPayDays: flatTaken.shown.map(billView),
        billsWithoutPayDaysOmitted: flatTaken.omitted,
        nextPayDay: payday.nextPayDay,
        nextPayDayWraps: payday.wrapsToNextMonth,
        dueBeforeNextPayDay: dueBeforeTaken.shown.map((x) => ({
          ...billView(x.bill),
          nextMonth: x.nextMonth,
        })),
        // Over EVERY bill in the window, so the naming allowance cannot move it.
        dueBeforeNextPayDayTotal: payday.dueBefore.reduce((t, x) => t + x.bill.amount, 0),
        dueBeforeNextPayDayOmitted: dueBeforeTaken.omitted,
        todayDay,
        rightNow: nowView.rightNow,
        planOn: nowView.planOn,
        nowUnavailable: nowView.unavailable,
        stillToComeOut: nowView.stillToComeOut,
        stillToComeUndated: nowView.stillToComeUndated,
      };
    }
  } catch (err) {
    failedRead("personal budget", err);
    personalFailed = true;
    personal = null;
  }

  // ⚠️ Both halves gone is `failed`. Returning "ok" with nothing in it would
  // render as "you have no budgets", which is the false all-clear this file
  // exists to prevent.
  if (projectsFailed && personalFailed) return { kind: "failed" };

  return { kind: "ok", projects, personal, projectsFailed, personalFailed, projectsTruncated };
}

// ── Rendering ───────────────────────────────────────────────────────────────

/** Whole dollars. Her trackers store and show round numbers. */
function money(n: number): string {
  const rounded = Math.round(n);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}$${Math.abs(rounded).toLocaleString("en-US")}`;
}

const FAILED_TEXT =
  "Kin couldn't read her budgets just now (a temporary hiccup, not a sign she has none). " +
  "Tell her the lookup failed and to try again in a moment. " +
  "⚠️ Do NOT say she has no budget, and do NOT name any amount — you were not given one.";

/**
 * ⚠️ The sentence that stops a floor being spoken as a total.
 *
 * A capped read under-reports spending, and under-reported spending is the one
 * error that makes her spend more. So a truncated money read may not produce a
 * remaining figure at all.
 */
const CAPPED_WARNING =
  " ⚠️ This read was TRUNCATED, so every number here is a FLOOR — she has spent AT LEAST this much. " +
  "Say so plainly, do not state a remaining amount, and offer to have her open the tracker for the full picture.";

function renderProject(p: ProjectBudgetView): string {
  const bits: string[] = [];
  bits.push(`• "${p.name}"`);
  if (p.completed) bits.push("(marked done)");

  // ⚠️ THE CAPPED BRANCH EXISTS BECAUSE A TRAILING WARNING CANNOT RETRACT A
  // NUMBER. PR #1809's second review round caught the first version emitting an
  // exact "$550 left" on a truncated read and relying on a sentence at the END of
  // the tool result to stop the model repeating it. By then the actionable figure
  // is already in the text, and this file's own header says that is the one error
  // that makes her spend money she does not have.
  //
  // And the arithmetic is worse than "a floor": `spent` is a lower bound, so
  // `total - spent` is an UPPER bound. "You have $900 left" off a capped read is
  // not conservative, it is optimistic in the direction that costs her money. So
  // remaining is not computed at all here.
  const atLeast = p.capped ? "at least " : "";
  if (p.total === null) {
    // ⚠️ No total set. Spending is a fact; "remaining" is not computable and the
    // app's own 0-minus-spent would read as her being over by everything.
    bits.push(
      `— spent ${atLeast}${money(p.spent)}, estimated ${atLeast}${money(p.estimated)}, no total set yet`,
    );
  } else if (p.capped) {
    bits.push(
      `— at least ${money(p.spent)} spent of ${money(p.total)} (REMAINING UNKNOWN — the line read was ` +
        `truncated, so spent is a floor and what's left cannot be computed), estimated at least ` +
        `${money(p.estimated)}`,
    );
  } else {
    const remaining = p.total - p.spent;
    const state = remaining < 0 ? `OVER by ${money(-remaining)}` : `${money(remaining)} left`;
    bits.push(
      `— ${money(p.spent)} spent of ${money(p.total)} (${state}), estimated ${money(p.estimated)}`,
    );
  }
  bits.push(`across ${atLeast}${p.lineCount} line${p.lineCount === 1 ? "" : "s"}`);
  let line = bits.join(" ");
  if (p.duplicateNameCount && p.duplicateNameCount > 1) {
    line +=
      ` ⚠️ She has ${p.duplicateNameCount} budgets with this exact name — do NOT present this as` +
      " the only one; name the numbers and ask which she means.";
  }
  if (p.topLines.length > 0) {
    const shown = p.topLines
      .map((l) => `${l.label} ${money(l.spent)}/${money(l.estimated)}`)
      .join("; ");
    const more = p.lineCount > p.topLines.length ? `, +${p.lineCount - p.topLines.length} more` : "";
    // ⚠️ "biggest" is now EARNED by a sort on spend. The first version said
    // "biggest lines" over rows ordered by `sort_order` — her own manual ordering
    // in the tracker — so Kin could describe whatever happened to be first as her
    // largest costs. Either the label or the order had to change; for a money
    // question the biggest lines are the useful ones, so the order did.
    line += `\n    biggest lines by spend (spent/estimated): ${shown}${more}`;
  }
  return line;
}

/**
 * "due the 5th", "due the last day of the month", or an honest blank.
 *
 * ⚠️ Reads the SHAPE of the shared label rather than comparing it to the string
 * "Last day". `dueDayLabel` owns that wording and may reword it; a leading digit
 * is what actually distinguishes an ordinal from a named day, so this keeps
 * reading correctly either way instead of silently emitting "due the Last day".
 */
function dueText(label: string): string {
  if (!label) return "no due day set";
  return /^\d/.test(label) ? `due the ${label}` : `due the ${label.toLowerCase()} of the month`;
}

/** One bill: "Mortgage $1,200, due the 5th". */
function renderBill(b: BudgetBillView): string {
  // ⚠️ No "at least" on an individual amount even on a capped read. A bill's
  // own figure came off a row that WAS read, so it is exact; only the
  // aggregates are floors. Hedging an exact number teaches the model to hedge
  // all of them, and then nothing it says carries weight.
  const final = b.finalMonth ? " (her LAST month for this bill)" : "";
  return `${b.name} ${money(b.amount)}, ${dueText(b.dueLabel)}${final}`;
}

/**
 * Her bills, grouped under the paycheck that covers them.
 *
 * ⚠️ On a CAPPED read this names bills and states no leftover, per paycheck or
 * otherwise. A bill list is not actionable the way a number is, and the point of
 * the cap rules is that `amount - billTotal` with a floored `billTotal` is a
 * CEILING: "you have $800 left on this paycheck" off a truncated read is
 * optimistic in the direction that costs her money. So the names survive and
 * every derived figure does not.
 */
function renderBillBreakdown(pv: PersonalBudgetView): string {
  if (pv.billCount === 0) return "";
  const out: string[] = [];

  // ── What is owed before the next paycheck ───────────────────────────────
  //
  // First, because it is the most actionable thing here and the reason the
  // per-paycheck leftover was removed: it answers "what's due before my next
  // payday" as a list of real obligations rather than as a balance that
  // overstates what she has.
  if (pv.nextPayDay !== null) {
    const when = pv.nextPayDayWraps
      ? `${ordinalLabel(pv.nextPayDay)} of next month`
      : `${ordinalLabel(pv.nextPayDay)}`;
    if (pv.dueBeforeNextPayDay.length === 0 && pv.dueBeforeNextPayDayOmitted === 0) {
      // A real answer, and a reassuring one. Not a failed read.
      out.push(`    BEFORE HER NEXT PAYCHECK (lands the ${when}): nothing else is due.`);
    } else {
      const n = pv.dueBeforeNextPayDay.length + pv.dueBeforeNextPayDayOmitted;
      out.push(
        `    BEFORE HER NEXT PAYCHECK (lands the ${when}): ${money(pv.dueBeforeNextPayDayTotal)}` +
          ` across ${n} bill${n === 1 ? "" : "s"}, counting from today (the` +
          ` ${ordinalLabel(pv.todayDay)}):`,
      );
      for (const b of pv.dueBeforeNextPayDay) {
        // ⚠️ "(next month)" or a wrapped window reads as a date already past.
        out.push(`      ${renderBill(b)}${b.nextMonth ? " (next month)" : ""}`);
      }
      if (pv.dueBeforeNextPayDayOmitted > 0) {
        out.push(`      +${pv.dueBeforeNextPayDayOmitted} more not listed here`);
      }
    }
  }

  if (pv.payDaysUnknown) {
    // ⚠️ The matcher assigns nothing when no pay day is known, so these bills
    // would otherwise be counted in the total and named nowhere.
    out.push(
      "    ⚠️ No paycheck has a pay day saved, so which paycheck covers which bill CANNOT be worked" +
        " out. Do not guess at one. Her bills in due order:",
    );
    for (const b of pv.billsWithoutPayDays) out.push(`      ${renderBill(b)}`);
    if (pv.billsWithoutPayDaysOmitted > 0) {
      out.push(`      +${pv.billsWithoutPayDaysOmitted} more not listed here`);
    }
    out.push(
      "    If she asks which paycheck covers something, tell her she has not saved her pay days yet" +
        " and that adding them under Budgets in the My Kin tab is what makes that answer possible.",
    );
  } else {
    for (const g of pv.groups) {
      // ⚠️ A bill TOTAL and never a leftover, on capped and clean reads alike.
      // The capped branch used to differ here only in suppressing the leftover;
      // now there is no leftover to suppress, so one shape serves both.
      const atLeast = pv.capped ? "at least " : "";
      const head =
        g.bills.length === 0 && g.billsOmitted === 0
          ? `${g.name} (${money(g.amount)}): no bills fall to this one`
          : `${g.name} (${money(g.amount)}): ${atLeast}${money(g.billTotal)} of bills`;
      out.push(`    ${head}`);
      for (const b of g.bills) out.push(`      ${renderBill(b)}`);
      if (g.billsOmitted > 0) out.push(`      +${g.billsOmitted} more not listed here`);
    }
    // ⚠️ The amounts above are two operands away from a wrong answer, so the
    // prohibition is stated rather than hoped for. It carries its REASON,
    // because an arbitrary rule gets dropped and an explained one holds.
    out.push(
      "    ⚠️ Do NOT subtract a paycheck's bills from its amount and call that what she has left on" +
        " it. That figure leaves out her spending and any bill with no due day, so it comes out" +
        " HIGHER than she really has. The only leftover is the month's, above.",
    );
  }

  if (pv.noDueDate.length > 0 || pv.noDueDateOmitted > 0) {
    // ⚠️ Counted in the month's total and under no paycheck, which is a thing
    // the model will otherwise get wrong in both directions: either adding
    // these on top of a total that already holds them, or treating a paycheck's
    // leftover as covering them.
    out.push(
      `    NO DUE DAY YET (${money(pv.noDueDateTotal)}, already inside the "out" total above and` +
        " under no paycheck):",
    );
    for (const b of pv.noDueDate) out.push(`      ${b.name} ${money(b.amount)}`);
    if (pv.noDueDateOmitted > 0) out.push(`      +${pv.noDueDateOmitted} more not listed here`);
    out.push(
      "    She can set a due day on these under Budgets in the My Kin tab, and then they land on a" +
        " paycheck.",
    );
  }

  const omitted =
    pv.groups.reduce((n, g) => n + g.billsOmitted, 0) +
    pv.noDueDateOmitted +
    pv.billsWithoutPayDaysOmitted;
  if (omitted > 0) {
    // ⚠️ Same rule as `projectsTruncated`: once the list is partial, an absence
    // from it stops being evidence.
    out.push(
      "    ⚠️ Not every bill is named above. Never tell her she has no bill for something on the" +
        " strength of this list; offer to look again for the one she means.",
    );
  }

  return out.join("\n");
}

function renderPersonal(pv: PersonalBudgetView): string {
  // ⚠️ Same rule as renderProject, and the arithmetic is even less forgiving:
  // `leftOver` is an incomplete inflow MINUS an incomplete outflow, so it has no
  // bound in either direction — it can be wrong high or wrong low and nothing
  // about the read says which. An exact "$1,600 left over" is therefore the most
  // dangerous single number this file can produce, and on a capped read it is not
  // produced.
  if (pv.capped) {
    return (
      renderRightNow(pv) +
      `HER MONTHLY BUDGET (${pv.month.slice(0, 7)}): at least ${money(pv.totalIn)} in, at least ` +
      `${money(pv.totalOut)} out. ⚠️ LEFT OVER IS UNKNOWN — this read was truncated, and an ` +
      `incomplete in minus an incomplete out is not a floor or a ceiling. Do NOT state what she has ` +
      `left; say the read was partial and offer the My Kin tab under Budgets for the full picture.\n` +
      `    in: at least ${money(pv.paycheckTotal)} from at least ${pv.paycheckCount} paycheck${pv.paycheckCount === 1 ? "" : "s"}` +
      ` + at least ${money(pv.incomeTotal)} other income\n` +
      `    out: at least ${money(pv.billTotal)} in at least ${pv.billCount} bill${pv.billCount === 1 ? "" : "s"}` +
      ` + at least ${money(pv.expenseTotal)} spending` +
      withBreakdown(pv)
    );
  }
  return (
    renderRightNow(pv) +
    `HER MONTHLY BUDGET (${pv.month.slice(0, 7)}): ${money(pv.totalIn)} in, ${money(pv.totalOut)} out, ` +
    `${money(pv.leftOver)} left over.\n` +
    `    in: ${money(pv.paycheckTotal)} from ${pv.paycheckCount} paycheck${pv.paycheckCount === 1 ? "" : "s"}` +
    ` + ${money(pv.incomeTotal)} other income\n` +
    `    out: ${money(pv.billTotal)} in ${pv.billCount} bill${pv.billCount === 1 ? "" : "s"}` +
    ` + ${money(pv.expenseTotal)} spending` +
    withBreakdown(pv)
  );
}

/**
 * RIGHT NOW and PLAN ON, the two figures #1929 added, as a leading block.
 *
 * First in the answer because it is what she asks when she is standing in a
 * shop. "Left over" is a whole-month projection and answers a different
 * question: on the 2nd it already counts a paycheck that has not landed.
 *
 * ⚠️ SUPPRESSED ENTIRELY ON A CAPPED READ. Both figures are differences of
 * sums that are FLOORS when the read truncates, and a difference of two floors
 * is bounded in neither direction. Same rule as the month's leftover, for the
 * same reason, and the same choice this file makes everywhere: suppress the
 * number rather than annotate it.
 */
function renderRightNow(pv: PersonalBudgetView): string {
  if (pv.capped) {
    return (
      `RIGHT NOW: ⚠️ UNKNOWN, because this read was truncated. Do not state what she has today or` +
      ` what she can plan on, and say the read was partial.\n`
    );
  }
  if (pv.nowUnavailable === "no-pay-days" || pv.rightNow === null || pv.planOn === null) {
    // ⚠️ A real answer, not a zero. With no pay day saved nothing can be placed
    // in time, so bills would subtract while no paycheck counted as arrived.
    return (
      `RIGHT NOW: ⚠️ CANNOT be worked out, because no paycheck has a pay day saved. Do not state a` +
      ` figure for what she has today. Tell her that adding her pay days under Budgets in the My Kin` +
      ` tab is what makes this answer possible.\n`
    );
  }
  const head = `RIGHT NOW (as of the ${ordinalLabel(pv.todayDay)}): ${money(pv.rightNow)}.`;

  // ⚠️ Nothing left to come reads badly as "after the $0 of bills", so it gets
  // its own sentence rather than a figure of zero inside one.
  const plan =
    pv.stillToComeOut === 0
      ? ` Nothing more is due before her next paycheck, so she can plan on all of it.`
      : ` After the ${money(pv.stillToComeOut)} of bills still due before her next paycheck,` +
        ` she can plan on ${money(pv.planOn)}.`;

  // ⚠️ Reconciles two numbers that otherwise contradict each other in the same
  // answer. The bill LIST below can only name bills it can place in time, so an
  // undated one is missing from it while still being counted here. Saying so is
  // cheaper than a reader deciding one of the two figures is a bug.
  const undated =
    pv.stillToComeUndated > 0
      ? ` ${money(pv.stillToComeUndated)} of that is bills with no due day, which is why the list` +
        ` below adds up to less.`
      : "";

  return (
    head +
    plan +
    undated +
    `\n    ⚠️ NOT a bank balance. This month starts at $0 and nothing carries over from last month,` +
    ` so it is what has moved through her budget since the 1st. Never call it her balance or imply` +
    ` it is what is in her account.\n`
  );
}

/** The bill breakdown as a suffix, or nothing when she has no bills. */
function withBreakdown(pv: PersonalBudgetView): string {
  const body = renderBillBreakdown(pv);
  return body ? `\n${body}` : "";
}

/** Turn an outcome into the tool result the model sees. */
export function renderBudgetRead(outcome: BudgetReadOutcome): string {
  if (outcome.kind === "failed") return FAILED_TEXT;

  if (outcome.kind === "miss") {
    // ⚠️ Same repair as get_todos' miss branch: a bare "not found" leaves her
    // told her own name is wrong with no way to learn the right one, so the
    // names she DOES have are part of the miss rather than a second call.
    const names = outcome.available.filter((n) => n.trim());
    if (names.length === 0) {
      return (
        `She has no budget called "${outcome.requested}" — in fact she has no project budgets at all yet. ` +
        "Tell her that, and that she can start one in the Budget Tracker whenever she wants."
      );
    }
    if (outcome.availableTruncated) {
      // ⚠️ Not an exhaustive "Hers are". With the list truncated, the budget she
      // meant may be one of the names that did not come back, so "the name you
      // used isn't one of yours" is no longer a claim this read can support.
      return (
        `She has no budget called "${outcome.requested}" among the ones that came back, and THE LIST IS ` +
        `INCOMPLETE — she has more budgets than these: ${names.join(", ")}. Do NOT tell her the name ` +
        `isn't one of hers; say you could only see some of them, name those, and offer to look again ` +
        `with her exact wording.`
      );
    }
    return (
      `She has no budget called "${outcome.requested}". Hers are: ${names.join(", ")}. ` +
      "Tell her the name she used isn't one of hers and NAME the ones she has, so she can pick."
    );
  }

  const parts: string[] = [];

  if (outcome.projects.length > 0) {
    // ⚠️ "at least N" when the read was truncated. Stating an exact count off a
    // capped read is the same error as stating a total off capped lines, one level
    // up — and it also makes an absence from this list look like a fact.
    const header = outcome.projectsTruncated
      ? `HER PROJECT BUDGETS (at least ${outcome.projects.length} — THE LIST IS INCOMPLETE, she has more):`
      : `HER PROJECT BUDGETS (${outcome.projects.length}):`;
    parts.push(`${header}\n${outcome.projects.map(renderProject).join("\n")}`);
  }
  if (outcome.personal) parts.push(renderPersonal(outcome.personal));

  // ⚠️ A half that FAILED is stated, never omitted. Silence about the personal
  // budget while reporting the projects reads as "there isn't one".
  if (outcome.projectsFailed) {
    parts.push(
      "⚠️ Her PROJECT budgets could not be read just now — say that honestly if it's relevant. " +
        "Do NOT say she has none and do NOT give a project amount.",
    );
  }
  if (outcome.personalFailed) {
    parts.push(
      "⚠️ Her MONTHLY budget could not be read just now — say that honestly if it's relevant. " +
        "Do NOT say she hasn't set one up and do NOT give a monthly amount.",
    );
  }

  if (parts.length === 0) {
    // Both reads worked and there is genuinely nothing. A real answer.
    return (
      "She has no project budgets and no budget set up for this month. That is a real answer — tell her " +
      "plainly, mention she can start one in the Budget Tracker, and do NOT say you can't see her budgets, " +
      "because you just looked."
    );
  }

  if (outcome.projects.length === 0 && !outcome.projectsFailed && !outcome.projectsTruncated) {
    parts.push("She has no project budgets at all — that is a real answer, not a failed read.");
  }
  if (!outcome.personal && !outcome.personalFailed) {
    parts.push("She has no budget set up for this month — a real answer, not a failed read.");
  }

  if (outcome.projectsTruncated) {
    parts.push(
      "⚠️ She has MORE project budgets than you were shown. Never say how many she has, and never say she " +
        "has no budget for something on the strength of this list — offer to check one by name instead.",
    );
  }

  const capped =
    outcome.projects.some((p) => p.capped) || (outcome.personal?.capped ?? false);

  return (
    parts.join("\n\n") +
    "\n\nAnswer the money question she actually asked, in a sentence or two, the way a friend who just " +
    "looked at it would. Give her the number — never tell her to go look it up herself when you can see it. " +
    "Never read every line back, and never name an amount you weren't given." +
    (capped ? CAPPED_WARNING : "")
  );
}
